import { lazy, Suspense, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Main, Wrapper } from './content.styles';

import PageWrapper from '../../page_wrapper.styles';

import Footer from '../../footer';
import LoadingText from '../../loading_text';
import PermissionGate from '../../permission_gate';
import ScrollToTop from '../../scroll_to_top';
import Title from '../../title';
import ConfigureBanner from './configure_banner';
import Header from './header';
import OpenWidgetHandler from './openwidget_handler';
import PlanSubscriptionIssuesBanner from './plan_subscription_issues_banner';
import SelectIntegration from './select_integration';

import { MODE } from '../../../env';

const shouldDisplayIngredients = MODE === 'saladstory-test' || MODE === 'saladstory-production';

const AccountRolesPage = lazy(() => import('./account_roles'));
const AccountsPage = lazy(() => import('./accounts'));
const BusinessesPage = lazy(() => import('./businesses'));
const CardsPage = lazy(() => import('./cards'));
const DevicesPage = lazy(() => import('./devices'));
const PlansPage = lazy(() => import('./plans'));
const HomePage = lazy(() => import('./home'));
const PlacesPage = lazy(() => import('./places'));
const IngredientsPage = lazy(() => import('./ingredients'));
const ProductsPage = lazy(() => import('./products'));
const SettingsPage = lazy(() => import('./settings'));
const StatsPage = lazy(() => import('./stats'));
const TransactionsPage = lazy(() => import('./transactions'));
const UsersPage = lazy(() => import('./users'));
const ConfigurePage = lazy(() => import('./configure'));
const ScannerPage = lazy(() => import('./scanner'));

export default function Content() {
  const wrapper = useRef<HTMLDivElement>(null);

  return (
    <Wrapper ref={wrapper}>
      <Title />
      <ScrollToTop wrapper={wrapper} />
      <Header />
      <PlanSubscriptionIssuesBanner />
      <Routes>
        <Route element={<ConfigureBanner />} path="" />
      </Routes>
      <Routes>
        <Route element={null} path="scanner" />
        <Route element={<OpenWidgetHandler />} path="*" />
      </Routes>
      <SelectIntegration />
      <Main>
        <Suspense fallback={<LoadingText />}>
          <PageWrapper>
            <Routes>
              <Route element={<HomePage />} path="" />
              <Route element={<ConfigurePage />} path="configure/*" />
              <Route element={<SettingsPage />} path="settings/*" />
              <Route
                element={
                  <PermissionGate permission="account_roles_view">
                    <AccountRolesPage />
                  </PermissionGate>
                }
                path="account_roles/*"
              />
              <Route
                element={
                  <PermissionGate permission="accounts_view">
                    <AccountsPage />
                  </PermissionGate>
                }
                path="accounts/*"
              />
              <Route
                element={
                  <PermissionGate permission="businesses_view">
                    <BusinessesPage />
                  </PermissionGate>
                }
                path="businesses/*"
              />
              <Route
                element={
                  <PermissionGate permission="cards_view">
                    <CardsPage />
                  </PermissionGate>
                }
                path="cards/*"
              />
              <Route
                element={
                  <PermissionGate permission="devices_view">
                    <DevicesPage />
                  </PermissionGate>
                }
                path="devices/*"
              />
              <Route
                element={
                  <PermissionGate permission="places_view">
                    <PlacesPage />
                  </PermissionGate>
                }
                path="places/*"
              />
              <Route
                element={
                  <PermissionGate permission="plans_view">
                    <PlansPage />
                  </PermissionGate>
                }
                path="plans/*"
              />
              {shouldDisplayIngredients ? (
                <Route
                  element={
                    <PermissionGate permission="ingredients_view">
                      <IngredientsPage />
                    </PermissionGate>
                  }
                  path="ingredients/*"
                />
              ) : null}
              <Route
                element={
                  <PermissionGate permission="products_view">
                    <ProductsPage />
                  </PermissionGate>
                }
                path="products/*"
              />
              <Route
                element={
                  <PermissionGate permission="scanner_view">
                    <ScannerPage />
                  </PermissionGate>
                }
                path="scanner/*"
              />
              <Route
                element={
                  <PermissionGate permission="stats_view">
                    <StatsPage />
                  </PermissionGate>
                }
                path="stats/*"
              />
              <Route
                element={
                  <PermissionGate permission="transactions_view">
                    <TransactionsPage />
                  </PermissionGate>
                }
                path="transactions/*"
              />
              <Route
                element={
                  <PermissionGate permission="users_view">
                    <UsersPage />
                  </PermissionGate>
                }
                path="users/*"
              />
              <Route element={<Navigate to="/" />} path="*" />
            </Routes>
          </PageWrapper>
        </Suspense>
      </Main>
      <Footer />
    </Wrapper>
  );
}
